import { Mol, Reaction } from '@moleculeone/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { styled } from '../../../../stitches.config';
import { Button } from '../../../controls/Button/Button';
import { Pagination } from '../../../controls/Pagination/Pagination';
import { Icon } from '../../../text/Icon/Icon';
import { InfoBox } from '../../../text/InfoBox/InfoBox';

import { SimplifiedTooltip } from '../../../containers/Tooltip';
import { fetchScifinderLink } from '../utils/fetchScifinderLink';
import {
  getReferenceReactions,
  getReferenceReactionsStats,
  RateLimitError,
  ReferenceReactions,
  ReferenceReactionsStats,
} from '../utils/referenceReactions';
import { Dialog, DialogClose, DialogContent, DialogContentInner, DialogOverlay } from './Dialog';
import { Reference } from './Reference';

const Header = styled('div', {
  padding: '$4 $5',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: 0,
  backgroundColor: '$slate4',
});

const HeaderSection = styled('div', {
  display: 'flex',
  gap: '$4',
  alignItems: 'center',
});

const SmartsInput = styled('input', {
  all: 'unset',
  width: '10rem',
  padding: '$3 $4',
  lineHeight: 1,
  backgroundColor: 'white',
  borderRadius: '$small',
  border: '1px solid $slate6',
});

const ReferencesContainer = styled('div', {
  padding: '$4 $5',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '$4',
});

const ServiceSelector = styled('div', {
  display: 'flex',
  gap: '$4',
  padding: '$3 $5',
  backgroundColor: 'white',
  borderRadius: '$small',
});

const REFERENCES_PER_PAGE = 20;

interface ReferencesProps {
  reactionSmiles: string;
  reaction: Reaction;
  scifinderToken?: string;
  smilesToMol?: (smiles: string) => Mol;
  rrConfig?: Record<string, string>;
}
export function References(props: ReferencesProps) {
  const { reactionSmiles, reaction, scifinderToken, smilesToMol, rrConfig } = props;

  const rrServices = Object.keys(rrConfig ?? {});

  const [activeRrService, setActiveRrService] = useState<string>(rrServices[0]);

  const [isOpen, setIsOpen] = useState(false);
  const [smarts, setSmarts] = useState<undefined | string>(undefined);
  const [references, setReferences] = useState<ReferenceReactions>();
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetchingLink, setIsFetchingLink] = useState(false);
  const [fetchingLinkError, setFetchingLinkError] = useState<string>();
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);

  const { data: stats, isLoading: isLoadingStats } = useQuery<ReferenceReactionsStats>({
    queryKey: ['references', reactionSmiles, reaction, rrConfig],
    queryFn: () => {
      return getReferenceReactionsStats({ reactionSmiles, reaction, rrConfig: rrConfig ?? {} });
    },
    enabled: !!rrConfig,
  });

  const fetchReferences = async (page = currentPage, service = activeRrService) => {
    if (!rrConfig?.[activeRrService]) {
      return;
    }
    try {
      setIsLoading(true);
      setReferences(undefined);
      const references = await getReferenceReactions({
        baseUrl: rrConfig[service],
        reactionSmiles,
        reaction,
        filter: smarts,
        offset: (page - 1) * REFERENCES_PER_PAGE,
        limit: REFERENCES_PER_PAGE,
        withSmiles: true,
      });
      setReferences(references);
    } catch (error) {
      if (error instanceof RateLimitError) {
        setRateLimitExceeded(true);
      } else {
        console.error('Error getting references', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const changePage = (page: number) => {
    setCurrentPage(page);
    fetchReferences(page);
  };

  const openInSciFinder = async (reactions?: ReferenceReactions['reactions']) => {
    if (!rrConfig?.[activeRrService]) {
      return;
    }
    setIsFetchingLink(true);
    try {
      let reactionsToFetch = reactions;
      if (!reactions) {
        reactionsToFetch = (
          await getReferenceReactions({
            baseUrl: rrConfig[activeRrService],
            reactionSmiles,
            reaction,
            filter: smarts,
            withSmiles: false,
          })
        ).reactions;
      }
      if (!reactionsToFetch || !scifinderToken) {
        return;
      }
      const url = await fetchScifinderLink(reactionsToFetch, scifinderToken);
      window.open(url, '_blank');
    } catch (error) {
      setFetchingLinkError(`Failed to fetch references: ${(error as Error).message}`);
    } finally {
      setIsFetchingLink(false);
    }
  };

  const toggleDialogOpen = () => {
    const willBeOpen = !isOpen;
    setIsOpen(willBeOpen);

    if (willBeOpen && !isLoading && !references) {
      fetchReferences();
    }
  };

  const noReferences = !isLoadingStats && stats && Object.values(stats).every(stats => stats.reference_count === 0);

  return (
    <>
      <SimplifiedTooltip content={noReferences ? 'No references found' : 'Show references'}>
        <Button
          size="small"
          theme={noReferences ? 'slate' : 'blue'}
          icon="menu_book"
          css={{
            '& > span': { fontSize: '16px !important' },
          }}
          disabled={isLoadingStats || !stats || noReferences}
          onClick={toggleDialogOpen}
        />
      </SimplifiedTooltip>
      {createPortal(
        <Dialog open={isOpen}>
          <DialogOverlay onClick={toggleDialogOpen}>
            <DialogContent onClick={e => e.stopPropagation()}>
              <DialogContentInner>
                <ServiceSelector>
                  {rrServices.map(service => (
                    <Button
                      key={service}
                      text={`${service} (${stats?.[service]?.reference_count ?? 0})`}
                      onClick={() => {
                        setActiveRrService(service);
                        fetchReferences(1, service);
                        setCurrentPage(1);
                      }}
                      theme={activeRrService === service ? 'purple' : 'slate'}
                      size="small"
                    />
                  ))}
                </ServiceSelector>
                {!scifinderToken && activeRrService === 'CAS' && (
                  <InfoBox type="error" icon="error">
                    No SciFinder token found
                  </InfoBox>
                )}

                <Header>
                  <HeaderSection>
                    <SmartsInput
                      value={smarts}
                      placeholder="Filter by SMARTS"
                      onChange={e => {
                        setSmarts(e.target.value ?? null);
                      }}
                    />
                    <Button
                      text="Filter"
                      icon="search"
                      size="small"
                      weight="minimal"
                      onClick={() => {
                        fetchReferences();
                      }}
                    />
                  </HeaderSection>

                  <HeaderSection>
                    {scifinderToken && activeRrService === 'CAS' && (
                      <Button
                        text={isFetchingLink ? 'Opening...' : 'Open all in SciFinder'}
                        icon="menu_book"
                        size="small"
                        theme="purple"
                        onClick={() => {
                          openInSciFinder();
                        }}
                      />
                    )}
                    <Pagination
                      currentPage={currentPage}
                      itemCount={stats?.[activeRrService]?.reference_count ?? 0}
                      pageSize={REFERENCES_PER_PAGE}
                      onChange={changePage}
                    />
                  </HeaderSection>
                </Header>

                {fetchingLinkError && (
                  <InfoBox type="error" icon="error">
                    {fetchingLinkError}
                  </InfoBox>
                )}

                <ReferencesContainer>
                  {rateLimitExceeded && (
                    <InfoBox type="error" icon="error" title="Rate limit exceeded">
                      <p>The rate limit for getting reference reaction SMILES has been exceeded.</p>
                      <Button
                        text={isFetchingLink ? 'Opening...' : 'Open all in SciFinder instead'}
                        icon="menu_book"
                        size="small"
                        theme="purple"
                        onClick={() => {
                          openInSciFinder();
                        }}
                      />
                    </InfoBox>
                  )}
                  {isLoading && <InfoBox icon="hourglass">Loading references...</InfoBox>}
                  {references && references.reactions.length === 0 && (
                    <InfoBox type="warning" icon="error">
                      No references found
                    </InfoBox>
                  )}
                  {!isLoading &&
                    references &&
                    references.reactions.map((reference, idx) => (
                      <Reference
                        key={idx}
                        reaction={reference}
                        smilesToMol={smilesToMol}
                        openInSciFinder={
                          scifinderToken
                            ? () => {
                                openInSciFinder([reference]);
                              }
                            : undefined
                        }
                      />
                    ))}
                </ReferencesContainer>

                <DialogClose aria-label="Close" onClick={toggleDialogOpen}>
                  <Icon icon="close" />
                </DialogClose>
              </DialogContentInner>
            </DialogContent>
          </DialogOverlay>
        </Dialog>,
        document.getElementById('widget-container') ?? document.body
      )}
    </>
  );
}
